import { defaultPropertyPanelId } from '@services/engine/engine'
import {
  EngineCommitChange,
  EngineMode,
  PropertyPanelPath,
  PropertyPanelSetStatus,
  SceneLayer,
  ScenePrimType,
  VariableColorModeType,
  VariableColorModeValues,
  Vec3d
} from '@services/engine/types'
import { degToRad } from '@services/engine/utils'
import Context from '@store/middleware/context'
import { PropertyPayload } from '@store/slices/sceneSlice'
import { RootState } from '@store/store'
import { select } from 'redux-saga/effects'

export default function* setSceneProperty(
  payload: PropertyPayload
): Generator<any, any, any> {
  if (!Context.Engine) return

  // These functions use the old document model, and hence the old undo/redo api
  if (payload.key === 'groupExpanded') {
    return yield Context.Engine.setModelPose(payload.value)
  }

  if (payload.key === 'primitiveScaleCorners') {
    return yield Context.Engine.setPrimitiveScaleCorners(payload.value)
  }

  if (payload.key === 'showAvatars') {
    return yield Context.Engine.toggleUsersAvatarVisibility()
  }

  if (payload.key === 'snappingEnabled') {
    return yield Context.Engine.setSnapping(payload.value)
  }

  const primitiveType: ScenePrimType = yield select(
    (state: RootState) => state.scene.primitiveType
  )

  /*
  ----------------- Primitive values -----------------
  ---------------------------------------------------
  ---------------------------------------------------
  */

  const { key, value, commit } = payload

  if (key === 'materialColor') {
    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    const propertyPath =
      activeLayer === '3D' ? 'shapeMaterial.pbrColor' : 'shapeMaterial.color'

    return yield setPropertyPanelValueWithDefaultId(
      propertyPath,
      hexColorToEngineColor(value),
      commit
    )
  }

  if (payload.key === 'materialRoughness') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.pbrRoughness',
      value,
      commit
    )
  }

  if (payload.key === 'materialMetalness') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.pbrMetalness',
      value,
      commit
    )
  }

  if (payload.key === 'materialOpacity') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.opacity',
      value,
      commit
    )
  }

  if (payload.key === 'materialReflective') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.pbrIsReflective',
      Boolean(value), // Some parts of the app (for example, userLibrarySaga will send this value as an int rather than a boolean). Cast to boolean here just to be safe. This should be sorted out at some point
      commit
    )
  }

  if (payload.key === 'blendType') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.blendMode',
      value,
      commit
    )
  }

  if (payload.key === 'blendAmount') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.blendShape',
      value,
      commit
    )
  }

  if (
    payload.key === 'transformPositionX' ||
    payload.key === 'transformPositionY' ||
    payload.key === 'transformPositionZ'
  ) {
    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    const { transformPositionX, transformPositionY, transformPositionZ } =
      yield select((state: RootState) => state.scene)

    let newPosition

    if (activeLayer === '3D') {
      newPosition = {
        x: transformPositionX,
        y: transformPositionY,
        z: transformPositionZ
      }
    } else {
      newPosition = {
        x: transformPositionX,
        y: transformPositionY
      }
    }

    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.location.position',
      newPosition,
      commit
    )
  }

  if (
    payload.key === 'transformRotationX' ||
    payload.key === 'transformRotationY' ||
    payload.key === 'transformRotationZ'
  ) {
    const { transformRotationX, transformRotationY, transformRotationZ } =
      yield select((state: RootState) => state.scene)

    let newRotation

    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    if (activeLayer === '3D') {
      // c++ side needs radians
      newRotation = {
        x: degToRad(transformRotationX),
        y: degToRad(transformRotationY),
        z: degToRad(transformRotationZ)
      }
    } else {
      newRotation = degToRad(transformRotationX)
    }

    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.location.rotation',
      newRotation,
      commit
    )
  }

  if (payload.key === 'transformScaleX') {
    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    if (activeLayer === '3D') {
      if (
        primitiveType === ScenePrimType.SOFT_BOX ||
        primitiveType === ScenePrimType.TRIANGLE
      ) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.profile.width',
          value,
          commit
        )
      }

      if (primitiveType === ScenePrimType.STAR) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.profile.radius',
          value,
          commit
        )
      }

      if (primitiveType == ScenePrimType.HORSESHOE) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.profile.angle',
          value,
          commit
        )
      }

      if (primitiveType === ScenePrimType.EGG) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.profile.radiusA',
          value,
          commit
        )
      }
    } else {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.width',
        value,
        commit
      )
    }
  }

  if (payload.key === 'transformScaleY') {
    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    if (activeLayer === '3D') {
      if (
        primitiveType === ScenePrimType.SOFT_BOX ||
        primitiveType === ScenePrimType.STAR ||
        primitiveType === ScenePrimType.TRIANGLE ||
        primitiveType === ScenePrimType.TEXT
      ) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.dimension.extrudeAmount',
          (value as number) / 2,
          commit
        )
      }

      if (primitiveType === ScenePrimType.HORSESHOE) {
        return yield setPropertyPanelValueWithDefaultId(
          'treeNode.dimension.extrudeAmount',
          value,
          commit
        )
      }
    } else {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.height',
        value,
        commit
      )
    }
  }

  if (payload.key === 'transformScaleZ') {
    if (primitiveType === ScenePrimType.SOFT_BOX) {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.height',
        value,
        commit
      )
    }

    if (primitiveType === ScenePrimType.HORSESHOE) {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.radius',
        value,
        commit
      )
    }

    if (primitiveType === ScenePrimType.TRIANGLE) {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.height',
        (value as number) * 2,
        commit
      )
    }

    if (primitiveType === ScenePrimType.EGG) {
      return yield setPropertyPanelValueWithDefaultId(
        'treeNode.profile.radiusB',
        (value as number) / 2,
        commit
      )
    }
  }

  if (payload.key === 'repeatType') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.repeatMode',
      value,
      commit
    )
  }

  if (payload.key === 'repeatX') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.repeatX',
      value,
      commit
    )
  }

  if (payload.key === 'repeatY') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.repeatY',
      value,
      commit
    )
  }

  if (payload.key === 'repeatZ') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.repeatZ',
      value,
      commit
    )
  }

  if (payload.key === 'repeatDistanceX') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.distanceX',
      value,
      commit
    )
  }

  if (payload.key === 'repeatDistanceY') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.distanceY',
      value,
      commit
    )
  }

  if (payload.key === 'repeatDistanceZ') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.distanceZ',
      value,
      commit
    )
  }

  if (payload.key === 'repeatAngleDirection') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.circularCopyAxis',
      value,
      commit
    )
  }

  if (payload.key === 'repeatAngle') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.circularCopyCount',
      value,
      commit
    )
  }

  if (payload.key === 'repeatDistance') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.circularCopyDistance',
      value,
      commit
    )
  }

  // An enum with values of 0 and 1 are used for this, so we cast to boolean here. Change this in other parts of the app at some point.
  if (payload.key === 'symmetryX') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.alongX',
      Boolean(value),
      commit
    )
  }

  if (payload.key === 'symmetryY') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.alongY',
      Boolean(value),
      commit
    )
  }

  if (payload.key === 'symmetryZ') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.alongZ',
      Boolean(value),
      commit
    )
  }

  if (payload.key === 'primitiveHole') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.thickness',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveRadial0') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.cornerRounding0',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveRadial1') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.cornerRounding1',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveRadial2') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.cornerRounding2',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveRadial3') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.cornerRounding3',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveStarPoints') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.sideCount',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveStarAngle') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.angle',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveStarCorners') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.roundingRadius',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeAngle') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.angle',
      value as number,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeRadius') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.radius',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeWidth') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.width',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeLength') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.length',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeThickness') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.thickness',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveHorseshoeCorner') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.corners',
      value,
      commit
    )
  }

  if (payload.key === 'triangleVertex') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.dartOffset',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveDimensionType') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.dimension.mode',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveCornerOne') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.dimension.bottomFillet',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveCornerTwo') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.dimension.topFillet',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveShell') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.onion',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveInflate') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.round',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveDistance') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.dimension.distance',
      value,
      commit
    )
  }

  if (payload.key === 'primitiveRotation') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.dimension.rotation',
      value as number,
      commit
    )
  }

  if (payload.key === 'materialEColorTop') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.exprColorTop',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'materialEColorLig') {
    const activeLayer: SceneLayer = yield select(
      (state: RootState) => state.scene.activeLayer
    )

    if (activeLayer === '3D') {
      return yield setPropertyPanelValueWithDefaultId(
        'shapeMaterial.exprColorLight',
        hexColorToEngineColor(value as string),
        commit
      )
    } else {
      return yield setPropertyPanelValueWithDefaultId(
        'shapeMaterial.color',
        hexColorToEngineColor(value as string),
        commit
      )
    }
  }

  if (payload.key === 'materialEColorSha') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.exprColorDark',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'materialESpecularSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.exprSpecularSize',
      value,
      commit
    )
  }

  if (payload.key === 'materialESpecularIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.exprSpecularIntensity',
      value,
      commit
    )
  }

  if (payload.key === 'materialIColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeColor',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'materialIEmissiveColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeEmissiveColor',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'materialIStrokeSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeStrokeSize',
      value,
      commit
    )
  }

  if (payload.key === 'materialIHighlightIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeSpecularIntensity',
      value,
      commit
    )
  }

  if (payload.key === 'materialIStrokeIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeNormalIntensity',
      value,
      commit
    )
  }

  if (payload.key === 'materialIColorVarIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeColorVariation',
      value,
      commit
    )
  }

  if (payload.key === 'materialIScaleX') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeScaleX',
      value,
      commit
    )
  }

  if (payload.key === 'materialIScaleY') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeScaleY',
      value,
      commit
    )
  }

  if (payload.key === 'materialIAngle') {
    return yield setPropertyPanelValueWithDefaultId(
      'shapeMaterial.illustrativeAngle',
      value,
      commit
    )
  }

  if (payload.key === 'variableColorMode') {
    const enumIndex = VariableColorModeValues.indexOf(
      value as VariableColorModeType
    )
    if (enumIndex === -1) return

    const styleMode = yield select((state: RootState) => state.scene.mode)
    if (styleMode === EngineMode.OUTLINE || styleMode === EngineMode.PIXEL) {
      return yield setPropertyPanelValueWithDefaultId(
        'shapeMaterial.exprMaterialMode',
        enumIndex,
        commit
      )
    } else {
      return yield setPropertyPanelValueWithDefaultId(
        'shapeMaterial.illustrativeMaterialMode',
        enumIndex,
        commit
      )
    }
  }

  /*
  ----------------- Global values -----------------
  ---------------------------------------------------
  ---------------------------------------------------
  */

  if (payload.key === 'frameEnabled') {
    return yield setPropertyPanelValueWithDefaultId(
      'frame.enabled',
      value,
      commit
    )
  }

  if (payload.key === 'frameType') {
    return yield setPropertyPanelValueWithDefaultId(
      'frame.frameType',
      value,
      commit
    )
  }

  if (payload.key === 'frameSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'frame.size',
      {
        x: payload.value.w ?? 0,
        y: payload.value.h ?? 0
      },
      commit
    )
  }

  if (payload.key === 'frameOpacity') {
    return yield setPropertyPanelValueWithDefaultId(
      'frame.opacity',
      value,
      commit
    )
  }

  if (payload.key === 'backgroundType') {
    return yield setPropertyPanelValueWithDefaultId(
      'background.gradientMode',
      value,
      commit
    )
  }

  if (payload.key === 'backgroundColorA') {
    return yield setPropertyPanelValueWithDefaultId(
      'background.colorA',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'backgroundColorB') {
    return yield setPropertyPanelValueWithDefaultId(
      'background.colorB',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'floorEnabled') {
    return yield setPropertyPanelValueWithDefaultId(
      'floor.enabled',
      value,
      commit
    )
  }

  if (payload.key === 'floorHeight') {
    return yield setPropertyPanelValueWithDefaultId(
      'floor.height',
      value,
      commit
    )
  }

  if (payload.key === 'cameraType') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.type',
      value,
      commit
    )
  }

  if (payload.key === 'cameraSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.orthoSize',
      value,
      commit
    )
  }

  if (payload.key === 'cameraDistortion') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.distortionAmount',
      value,
      commit
    )
  }

  if (payload.key === 'cameraFocalLength') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.perspFocalLength',
      value,
      commit
    )
  }

  if (payload.key === 'cameraFocalPlane') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.dofFocalPlane',
      value,
      commit
    )
  }

  if (payload.key === 'cameraAperture') {
    return yield setPropertyPanelValueWithDefaultId(
      'cameraLens.dofAperture',
      value,
      commit
    )
  }

  if (payload.key === 'mode') {
    // Subtract 1 from value because the engine returns a value that is 1 less than what it is on the JS side (this enum in the engine starts at 0, whereas on the JS side it starts at 1)
    return yield setPropertyPanelValueWithDefaultId(
      'style.style',
      payload.value - 1,
      commit
    )
  }

  if (payload.key === 'modeExpressiveOutline') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.exprOutline',
      value,
      commit
    )
  }

  if (payload.key === 'modePixelSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.pixelArtPixelCount',
      value,
      commit
    )
  }

  if (payload.key === 'modeOutlineColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.exprColor',
      hexColorToEngineColor(value as string),
      commit
    )
  }

  if (payload.key === 'pixelOutlineEnabled') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.pixelArtOutlineThickness',
      // On the c++ side, outline thickness is an int, so cast this
      // to a number so that outline thickness is either 1 or 0
      Number(value),
      commit
    )
  }

  if (payload.key === 'modeIllustrativeLightTexture') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusLightTexture',
      value,
      commit
    )
  }

  if (payload.key === 'modeIllustrativeShadowTexture') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusShadowTexture',
      value,
      commit
    )
  }

  if (payload.key === 'modeIllustrativeGlobalStrokeSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusGlobalStrokeSize',
      value,
      commit
    )
  }

  if (payload.key === 'modeIllustrativeFilterStrength') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusFilterStrength',
      value
    )
  }

  if (payload.key === 'modeIllustrativeHighlightIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusLightIntensity',
      value
    )
  }

  if (payload.key === 'modeIllustrativeAmbientOcclusionIntensity') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusAOIntensity',
      value
    )
  }

  if (payload.key === 'modeIllustrativeEdgeBlendStrength') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusEdgeStrength',
      value
    )
  }

  if (payload.key === 'modeIllustrativeOutlineEnabled') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusOutlineEnabled',
      value
    )
  }

  if (payload.key === 'modeIllustrativeOutlineTolerance') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusOutlineTolerance',
      value
    )
  }

  if (payload.key === 'modeIllustrativeOutlineColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusOutlineColor',
      hexColorToEngineColor(value as string)
    )
  }

  if (payload.key === 'modeIllustrativeHighlightColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusHighlightColor',
      hexColorToEngineColor(value as string)
    )
  }

  if (payload.key === 'modeIllustrativeShadowColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusShadowColor',
      hexColorToEngineColor(value as string)
    )
  }

  if (payload.key === 'modeIllustrativeSkyColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusSkyColor',
      hexColorToEngineColor(value as string)
    )
  }

  if (payload.key === 'modeIllustrativeBounceColor') {
    return yield setPropertyPanelValueWithDefaultId(
      'style.illusBounceColor',
      hexColorToEngineColor(value as string)
    )
  }

  if (payload.key === 'elementName') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.standard.name',
      value
    )
  }

  if (payload.key === 'textFont') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.font',
      value
    )
  }

  if (payload.key === 'textLetterSpacing') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.letterSpacing',
      value
    )
  }

  if (payload.key == 'textLeadingType') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.leadingType',
      value
    )
  }

  if (payload.key === 'textLineHeight') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.lineHeight',
      value
    )
  }

  if (payload.key === 'textSize') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.size',
      value
    )
  }

  if (payload.key === 'textCopy') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.profile.text',
      value
    )
  }

  if (payload.key === 'drawMaterial') {
    return yield setPropertyPanelValueWithDefaultId(
      'treeNode.drawMaterial',
      value
    )
  }

  if (payload.key === 'lightOcclusionDistance') {
    return yield setPropertyPanelValueWithDefaultId(
      'lightOcclusion.distance',
      value,
      commit
    )
  }

  if (payload.key === 'lightAngle1') {
    const lightAngle1InRad: number = degToRad(value as number)
    const lightAngle2InRad: number = degToRad(
      yield select((state: RootState) => state.scene.lightAngle2)
    )

    return yield setPropertyPanelValueWithDefaultId(
      'directionalLight.direction',
      calculateDirectionalLightFromAngles(lightAngle1InRad, lightAngle2InRad),
      commit
    )
  }

  if (payload.key === 'lightAngle2') {
    const lightAngle1InRad: number = degToRad(
      yield select((state: RootState) => state.scene.lightAngle1)
    )

    const lightAngle2InRad: number = degToRad(value as number)

    return yield setPropertyPanelValueWithDefaultId(
      'directionalLight.direction',
      calculateDirectionalLightFromAngles(lightAngle1InRad, lightAngle2InRad),
      commit
    )
  }
}

// Angle must be in radians
function calculateDirectionalLightFromAngles(
  angle1InRad: number,
  angle2InRad: number
) {
  const direction: Vec3d = {
    x: Math.cos(angle1InRad) * Math.sin(angle2InRad),
    y: Math.cos(angle2InRad),
    z: Math.sin(angle1InRad) * Math.sin(angle2InRad)
  }

  return direction
}

function hexColorToEngineColor(hex: string): Vec3d {
  if (!Context.Engine)
    throw new Error(
      'Engine must be intialized before calling hexColorToEngineColor'
    )

  const color: [number, number, number] =
    Context.Engine.hexColorToEngineColor(hex)

  return {
    x: color[0],
    y: color[1],
    z: color[2]
  }
}

function setPropertyPanelValueWithDefaultId(
  path: PropertyPanelPath,
  val: any,
  commit?: EngineCommitChange
): PropertyPanelSetStatus | undefined {
  if (!Context.Engine) return
  return Context.Engine.setPropertyPanelValue(
    path,
    val,
    defaultPropertyPanelId,
    commit
  )
}
